"use client";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRightIcon, GithubIcon } from "lucide-react";
import dynamic from "next/dynamic";
import { useState, useEffect } from "react";

// Lazy load Lanyard after page is fully loaded for better performance
const Lanyard = dynamic(() => import("./Lanyard"), {
  ssr: false,
  loading: () => null, // Don't show anything while loading
});

import Link from "next/link";
import { handleSignUpClick } from "@/lib/tracking";

// const developers = [
//   "/users/User-1.png",
//   "/users/User-2.png",
//   "/users/User-3.png",
//   "/users/User-4.png",
//   "/users/User-5.png",
//   "/users/User-3.png",
//   "/users/User-4.png",
//   "/users/User-5.png",
// ];

const HeroSection = () => {
  const shouldReduceMotion = useReducedMotion();

  // State to control when Lanyard should load (after page is fully loaded)
  const [shouldLoadLanyard, setShouldLoadLanyard] = useState(false);
  // Initialize isBelowLg correctly to prevent prop changes on first render
  const [isBelowLg, setIsBelowLg] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 1023px)").matches
      : false,
  );

  // Hide 3D lanyard on mobile + tablets (<1024px)
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 1023px)");
    const onChange = () => setIsBelowLg(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  // Load Lanyard immediately after component mounts (dynamic import already provides delay)
  useEffect(() => {
    setShouldLoadLanyard(true);
  }, []);

  return (
    <section
      id="hero"
      className="relative min-h-screen bg-gradient-to-br bg-[#020F2A] overflow-hidden"
    >
      {/* Background effects without absolute positioning */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_#3BCF8E_0%,_transparent_50%)] opacity-25 blur-[30px] pointer-events-none" />

      {/* Main content with relative positioning */}
      <div className="relative container mx-auto px-1 md:px-4 lg:px-32 pb-24 pt-0 lg:pt-24 min-h-screen flex flex-col items-center lg:items-start justify-center z-10">
        {/* Lanyard container - reserves space on mobile to prevent layout shift */}
        {/* Use CSS media query to avoid hydration mismatch */}
        <div className="w-full h-0 lg:h-0 max-lg:h-[360px] flex justify-center max-lg:translate-y-[3vh]">
          {shouldLoadLanyard && (
            <Lanyard
              key="hero-lanyard"
              position={isBelowLg ? [0, 0, 15] : [0, 0, 20]}
              variant={isBelowLg ? "inline" : "overlay"}
              gravity={[0, -40, 0]}
            />
          )}
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="w-full text-center lg:text-left flex flex-col items-center lg:items-start max-md:space-y-6 md:space-y-8"
        >
          <h1 className="w-full max-w-[70%] text-2xl md:text-5xl lg:text-5xl xl:text-7xl font-bold text-white leading-tight bg-gradient-to-r from-[#3BCF8E] to-[#2DA771] bg-clip-text text-transparent">
            Smarter Code Reviews
            <br />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-300">
              in <span className="text-primary">Seconds</span>, Not Hours
            </span>
          </h1>

          <p className="w-full lg:max-w-[60%] text-gray-300 text-lg md:text-xl lg:text-2xl mx-auto lg:mx-0 px-4 lg:px-0">
            Infinitcode.ai helps you offload code reviews to an AI agent that
            find bugs, code performance issues, and suggests fixes.
          </p>
          <motion.div
            className="relative inline-block self-center lg:self-start"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            {/* Outer glow ring */}
            <motion.div
              className="absolute -inset-1 rounded-3xl opacity-75"
              style={{
                background:
                  "linear-gradient(135deg, #3BCF8E, #2DA771, #3BCF8E)",
                filter: "blur(12px)",
              }}
              animate={
                !shouldReduceMotion
                  ? {
                      opacity: [0.5, 0.8, 0.5],
                    }
                  : {}
              }
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />

            {/* Animated border */}
            <motion.div
              className="absolute -inset-0.5 rounded-3xl"
              style={{
                background:
                  "linear-gradient(135deg, #3BCF8E, #2DA771, #3BCF8E, #2DA771)",
                backgroundSize: "200% 200%",
              }}
              animate={
                !shouldReduceMotion
                  ? {
                      backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
                    }
                  : {}
              }
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "linear",
              }}
            />

            {/* Main button container */}
            <Link
              onClick={handleSignUpClick}
              href={"https://app.infinitcode.ai/"}
              target="_blank"
              className="block"
            >
              <motion.button
                whileHover={!shouldReduceMotion ? { y: -2 } : undefined}
                whileTap={!shouldReduceMotion ? { y: 0 } : undefined}
                className="group relative overflow-hidden bg-gradient-to-r from-[#3BCF8E] to-[#2DA771] text-white px-8 py-4 lg:px-10 lg:py-5 rounded-3xl text-base lg:text-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2"
                style={{
                  boxShadow:
                    "0 10px 40px rgba(59, 207, 142, 0.4), 0 0 30px rgba(59, 207, 142, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
                }}
              >
                {/* Shimmer effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                  initial={{ x: "-100%" }}
                  animate={!shouldReduceMotion ? { x: "200%" } : {}}
                  transition={{
                    repeat: Infinity,
                    duration: 3,
                    ease: "linear",
                  }}
                />

                {/* Pulsing inner glow */}
                <motion.div
                  className="absolute inset-0 rounded-3xl"
                  style={{
                    background:
                      "radial-gradient(circle at center, rgba(255, 255, 255, 0.1) 0%, transparent 70%)",
                  }}
                  animate={
                    !shouldReduceMotion
                      ? {
                          opacity: [0.3, 0.6, 0.3],
                        }
                      : {}
                  }
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />

                {/* Content */}
                <span className="relative z-10 font-semibold tracking-wide">
                  Try it for free
                </span>
                {!shouldReduceMotion && (
                  <motion.span
                    className="relative z-10 inline-block"
                    animate={{ x: [0, 4, 0] }}
                    transition={{
                      repeat: Infinity,
                      duration: 1.5,
                      ease: "easeInOut",
                    }}
                  >
                    <ArrowRightIcon className="h-5 w-5" />
                  </motion.span>
                )}
              </motion.button>
            </Link>
          </motion.div>
          <div className="flex items-center gap-3 text-md text-gray-400">
            <p>2-click install</p>
            <span>|</span>
            <GithubIcon className="size-5" />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
